import React from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { TaxonomyFieldsEditor } from '../finance/TaxonomyFieldsEditor';

export const INCOME_SOURCES = [
  'school_fees', 'donations', 'grants', 'chicken_sales', 'garden_produce',
  'masonry_sales', 'loan_interest', 'fundraising', 'maize_sales', 'sponsorship', 'training_fees',
];
export const PROJECT_TYPES = [
  'chicken_farming', 'school_garden', 'masonry', 'women_development',
];
export const STAFF_CATEGORIES = ['administrator', 'teaching', 'non_teaching'];
export const STUDENT_SCHOOLS = ['suubi_primary', 'suubi_secondary'];
export const TRAINING_CATEGORIES = ['basic_computer_skills', 'coding', 'farming', 'soap_making', 'fish_farming', 'carpentry', 'music', 'robotics', 'other'];

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs">{label}</Label>
    {children}
  </div>
);

const Grid2 = ({ children }) => <div className="grid grid-cols-2 gap-3">{children}</div>;

const OptionSelect = ({ value, onChange, options, placeholder = 'Select', testId }) => (
  <Select value={value || ''} onValueChange={onChange}>
    <SelectTrigger data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>{options.map((o) => <SelectItem key={o} value={o}>{o.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
  </Select>
);

const ProjectSelect = ({ value, onChange, projects }) => (
  <Select value={value || ''} onValueChange={(v) => onChange(v === '__none__' ? '' : v)}>
    <SelectTrigger><SelectValue placeholder="Optional" /></SelectTrigger>
    <SelectContent>
      <SelectItem value="__none__">None</SelectItem>
      {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
    </SelectContent>
  </Select>
);

const IncomeFields = ({ form, setField, projects, today }) => (
  <>
    <Grid2>
      <Field label="Source *">
        <OptionSelect value={form.source} onChange={(v) => setField('source', v)} options={INCOME_SOURCES} testId="qe-income-source" />
      </Field>
      <Field label="Amount (UGX) *">
        <Input type="number" value={form.amount || ''} onChange={(e) => setField('amount', e.target.value)} required />
      </Field>
    </Grid2>
    <Grid2>
      <Field label="Date">
        <Input type="date" value={form.date || today} onChange={(e) => setField('date', e.target.value)} />
      </Field>
      <Field label="Related Project">
        <ProjectSelect value={form.related_project} onChange={(v) => setField('related_project', v)} projects={projects} />
      </Field>
    </Grid2>
    <Field label="Description">
      <Input value={form.description || ''} onChange={(e) => setField('description', e.target.value)} />
    </Field>
  </>
);

const ExpenseFields = ({ form, setField, setForm, projects, taxonomy, today }) => (
  <>
    <TaxonomyFieldsEditor
      taxonomy={taxonomy}
      idPrefix="qep"
      value={{
        category_group: form.category_group || '',
        subcategory: form.subcategory || '',
        women_activity: form.women_activity || '',
        details: form.details || {},
      }}
      onChange={(v) => setForm((prev) => ({ ...prev, ...v }))}
    />
    <Grid2>
      <Field label="Amount (UGX) *">
        <Input type="number" value={form.amount || ''} onChange={(e) => setField('amount', e.target.value)} required />
      </Field>
      <Field label="Date">
        <Input type="date" value={form.date || today} onChange={(e) => setField('date', e.target.value)} />
      </Field>
    </Grid2>
    <Grid2>
      <Field label="Related Project">
        <ProjectSelect value={form.related_project} onChange={(v) => setField('related_project', v)} projects={projects} />
      </Field>
      <Field label="Description">
        <Input value={form.description || ''} onChange={(e) => setField('description', e.target.value)} />
      </Field>
    </Grid2>
  </>
);

const LoanFields = ({ form, setField }) => (
  <>
    <Grid2>
      <Field label="Borrower Name *"><Input value={form.borrower_name || ''} onChange={(e) => setField('borrower_name', e.target.value)} required /></Field>
      <Field label="Phone *"><Input value={form.borrower_phone || ''} onChange={(e) => setField('borrower_phone', e.target.value)} required /></Field>
    </Grid2>
    <Grid2>
      <Field label="Business Type"><Input value={form.business_type || ''} onChange={(e) => setField('business_type', e.target.value)} /></Field>
      <Field label="Amount (UGX) *"><Input type="number" value={form.loan_amount || ''} onChange={(e) => setField('loan_amount', e.target.value)} required /></Field>
    </Grid2>
    <Grid2>
      <Field label="Interest %"><Input type="number" value={form.interest_rate || '10'} onChange={(e) => setField('interest_rate', e.target.value)} /></Field>
      <Field label="Duration (months)"><Input type="number" value={form.duration_months || '6'} onChange={(e) => setField('duration_months', e.target.value)} /></Field>
    </Grid2>
  </>
);

const MaizePurchaseFields = ({ form, setField, farmers, today }) => (
  <>
    <Field label="Farmer *">
      <Select value={form.farmer_id || ''} onValueChange={(v) => setField('farmer_id', v)}>
        <SelectTrigger><SelectValue placeholder="Choose farmer" /></SelectTrigger>
        <SelectContent>{farmers.map((f) => <SelectItem key={f.id} value={f.id}>{f.farmer_name}</SelectItem>)}</SelectContent>
      </Select>
    </Field>
    <Grid2>
      <Field label="Quantity (kg) *"><Input type="number" value={form.quantity_kg || ''} onChange={(e) => setField('quantity_kg', e.target.value)} required /></Field>
      <Field label="Price / kg *"><Input type="number" value={form.price_per_kg || ''} onChange={(e) => setField('price_per_kg', e.target.value)} required /></Field>
    </Grid2>
    <Field label="Date"><Input type="date" value={form.purchase_date || today} onChange={(e) => setField('purchase_date', e.target.value)} /></Field>
  </>
);

const SponsorshipChildFields = ({ form, setField }) => (
  <>
    <Grid2>
      <Field label="Child Name *"><Input value={form.name || ''} onChange={(e) => setField('name', e.target.value)} required /></Field>
      <Field label="Grade"><Input value={form.grade || ''} onChange={(e) => setField('grade', e.target.value)} placeholder="e.g., P3" /></Field>
    </Grid2>
    <Grid2>
      <Field label="School"><Input value={form.school || ''} onChange={(e) => setField('school', e.target.value)} /></Field>
      <Field label="Sponsor Name"><Input value={form.sponsor_name || ''} onChange={(e) => setField('sponsor_name', e.target.value)} /></Field>
    </Grid2>
    <Field label="Initial Balance (UGX)"><Input type="number" value={form.account_balance || ''} onChange={(e) => setField('account_balance', e.target.value)} /></Field>
  </>
);

const TrainingSessionFields = ({ form, setField, today }) => (
  <>
    <Field label="Title *"><Input value={form.title || ''} onChange={(e) => setField('title', e.target.value)} required /></Field>
    <Grid2>
      <Field label="Category *">
        <OptionSelect value={form.category} onChange={(v) => setField('category', v)} options={TRAINING_CATEGORIES} />
      </Field>
      <Field label="Instructor"><Input value={form.instructor || ''} onChange={(e) => setField('instructor', e.target.value)} /></Field>
    </Grid2>
    <Grid2>
      <Field label="Start Date *"><Input type="date" value={form.start_date || today} onChange={(e) => setField('start_date', e.target.value)} required /></Field>
      <Field label="End Date"><Input type="date" value={form.end_date || ''} onChange={(e) => setField('end_date', e.target.value)} /></Field>
    </Grid2>
  </>
);

const StaffFields = ({ form, setField }) => (
  <>
    <Grid2>
      <Field label="Name *"><Input value={form.name || ''} onChange={(e) => setField('name', e.target.value)} required /></Field>
      <Field label="Category *">
        <OptionSelect value={form.staff_category} onChange={(v) => setField('staff_category', v)} options={STAFF_CATEGORIES} />
      </Field>
    </Grid2>
    <Grid2>
      <Field label="Phone"><Input value={form.phone || ''} onChange={(e) => setField('phone', e.target.value)} /></Field>
      <Field label="Salary (UGX)"><Input type="number" value={form.salary || ''} onChange={(e) => setField('salary', e.target.value)} /></Field>
    </Grid2>
    {form.staff_category === 'teaching' && (
      <Grid2>
        <Field label="Subject"><Input value={form.subject || ''} onChange={(e) => setField('subject', e.target.value)} /></Field>
        <Field label="Class"><Input value={form.class_taught || ''} onChange={(e) => setField('class_taught', e.target.value)} /></Field>
      </Grid2>
    )}
    {form.staff_category === 'administrator' && (
      <Grid2>
        <Field label="Title"><Input value={form.title || ''} onChange={(e) => setField('title', e.target.value)} /></Field>
        <Field label="Role"><Input value={form.admin_role || ''} onChange={(e) => setField('admin_role', e.target.value)} /></Field>
      </Grid2>
    )}
    {form.staff_category === 'non_teaching' && (
      <Field label="Position"><Input value={form.position || ''} onChange={(e) => setField('position', e.target.value)} /></Field>
    )}
  </>
);

const StudentFields = ({ form, setField }) => (
  <>
    <Grid2>
      <Field label="Name *"><Input value={form.name || ''} onChange={(e) => setField('name', e.target.value)} required /></Field>
      <Field label="Class *"><Input value={form.student_class || ''} onChange={(e) => setField('student_class', e.target.value)} placeholder="e.g., P4, S2" required /></Field>
    </Grid2>
    <Field label="School *">
      <OptionSelect value={form.school_type} onChange={(v) => setField('school_type', v)} options={STUDENT_SCHOOLS} />
    </Field>
    <Grid2>
      <Field label="Parent Name(s)"><Input value={form.parent_names || ''} onChange={(e) => setField('parent_names', e.target.value)} /></Field>
      <Field label="Parent Contact"><Input value={form.parent_contact || ''} onChange={(e) => setField('parent_contact', e.target.value)} /></Field>
    </Grid2>
  </>
);

const ProjectFields = ({ form, setField, today }) => (
  <>
    <Grid2>
      <Field label="Project Name *"><Input value={form.name || ''} onChange={(e) => setField('name', e.target.value)} required /></Field>
      <Field label="Type *">
        <OptionSelect value={form.project_type} onChange={(v) => setField('project_type', v)} options={PROJECT_TYPES} />
      </Field>
    </Grid2>
    <Grid2>
      <Field label="Start Date *"><Input type="date" value={form.start_date || today} onChange={(e) => setField('start_date', e.target.value)} required /></Field>
      <Field label="Budget (UGX)"><Input type="number" value={form.budget || ''} onChange={(e) => setField('budget', e.target.value)} /></Field>
    </Grid2>
    <Field label="Description"><Textarea value={form.description || ''} onChange={(e) => setField('description', e.target.value)} rows={2} /></Field>
  </>
);

const FIELDS_BY_TYPE = {
  income: IncomeFields,
  expense: ExpenseFields,
  loan: LoanFields,
  maize_purchase: MaizePurchaseFields,
  sponsorship_child: SponsorshipChildFields,
  training_session: TrainingSessionFields,
  staff: StaffFields,
  student: StudentFields,
  project: ProjectFields,
};

export const QuickEntryFields = (props) => {
  const Fields = FIELDS_BY_TYPE[props.entryType];
  return Fields ? <Fields {...props} /> : null;
};
